import {
  attachIntegration,
  create,
  destroy,
  emailEstimate,
  getEstimateCommission,
  IEstimatePayload,
  list,
  load,
  pattern,
  update,
  validateCommission,
} from "common/api/estimate.service";
import { addFlashAlert } from "common/components/FlashAlert";
import { formatDateOnly } from "common/helpers/Date";
import { handlebars } from "common/helpers/Handlebars";
import { DocumentDisplayType, DocumentRecord } from "common/records/Document";
import { EstimateRecord, IEstimateData } from "common/records/Estimate";
import { EstimateGroupRecord } from "common/records/EstimateGroup";
import { EstimateLineItemRecord } from "common/records/EstimateLineItem";
import { EstimateLineItemOptionRecord } from "common/records/EstimateLineItemOption";
import { LocalCalcFinanceOptionProviders, localCalculations } from "common/records/FinanceOption";
import { ImageDisplayType, ImageRecord } from "common/records/Image";
import { OpeningEstimationRecord } from "common/records/OpeningEstimation";
import { TokenKind } from "common/records/Token";
import { EstimateParamsRecord as EmailParamsRecord } from "common/records/index/EstimateEmailParams";
import { EstimateParamsRecord } from "common/records/index/EstimateParams";
import { RootState, ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { DocumentAsyncActions, SILENT_DOCUMENT_CONTEXT } from "common/reducers/document.async.actions";
import { EstimateActions } from "common/reducers/estimate.actions";
import { EstimateEditorActions, EstimateKind } from "common/reducers/estimateEditor.actions";
import { EstimateEditorAsyncActions } from "common/reducers/estimateEditor.async.actions";
import { EstimateLineItemActions } from "common/reducers/estimateLineItem.actions";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { ImageAsyncActions, SILENT_IMAGE_CONTEXT } from "common/reducers/image.async.actions";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { byId as documentsById, document as documentSelector } from "common/selectors/document.selectors";
import { estimate, lastSavedById, unsavedId } from "common/selectors/estimate.selectors";
import { toEstimateFinanceOptionsJson } from "common/selectors/estimateFinanceOption.selectors";
import { estimateGroup } from "common/selectors/estimateGroup.selectors";
import {
  estimateLineItem,
  lastSavedLineItemsById,
  byId as lineItemsById,
} from "common/selectors/estimateLineItem.selectors";
import { estimateLineItemOption } from "common/selectors/estimateLineItemOption.selectors";
import { filteredFinanceOptions } from "common/selectors/financeOption.selectors";
import { byId as imagesById, image as imageSelector, imageUrlsFromIds } from "common/selectors/image.selectors";
import { currentJob, currentJobId } from "common/selectors/job.selectors";
import { openingEstimation as openingEstimationSelector } from "common/selectors/openingEstimation.selectors";
import { commissionCustomConfig, currentOrgSettingsConfig } from "common/selectors/org.selectors";
import { pattern as patternSelector, patternTestData } from "common/selectors/pattern.selectors";
import { form } from "common/selectors/rsfForm.selectors";
import { RootDispatchType, RootGetState } from "common/store";
import { IdType, Nullable } from "common/types";
import { Map as ImmutableMap, List } from "immutable";
import { IGlobalRequestOptions } from "common/api/Fetcher";

/**
 * Human-readable document title for a given estimate `kind`. Drives kind-aware success toasts
 * ("Inspection saved successfully" vs. "Estimate saved successfully") so a single save/email
 * flow reads correctly across the estimator, inspection, and survey tabs.
 */
export const kindTitle = (kind: EstimateKind | undefined): string => {
  switch (kind) {
    case "inspection":
      return "Inspection";
    case "survey":
      return "Survey";
    default:
      return "Estimate";
  }
};

/** Maps an email `doc_type` to the estimate `kind` for kind-aware email toasts. */
const docTypeKind = (docType: string | undefined): EstimateKind =>
  docType === "inspection" ? "inspection" : "default";

export const EstimateAsyncActions = {
  listEstimates: (queryParams: EstimateParamsRecord, options?: IGlobalRequestOptions): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      const jobId = queryParams.get("job_id");

      if (!jobId) {
        addFlashAlert("danger", "No job selected");
        return;
      }

      const modelName = "estimateOverviews";
      dispatch(IdsManagementActions.fetchModel(modelName));

      try {
        const { estimates, meta } = await (options ? list(queryParams, options) : list(queryParams));

        dispatch(EstimateActions.receiveEstimateOverviews(estimates));

        const ids = List(estimates.map((est) => est.id));
        dispatch(IdsManagementActions.receiveModel(modelName, ids, meta?.pagination));
      } catch (error) {
        handleErrors(error);
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      }
    };
  },

  /**
   * Archives (soft-deletes) an estimate/inspection and removes the estimate
   * from Redux. Resolves true on success so callers handle the result with
   * after-deletion actions appropriately, if required.
   */
  deleteEstimate: (jobId: number, estimateId: number): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        await destroy(jobId, estimateId);
        dispatch(EstimateActions.removeEstimate(estimateId));
        return true;
      } catch (error) {
        handleErrors(error);
        return false;
      }
    };
  },

  getEstimate: (
    jobId: number,
    estimateId: number,
    setCurrentEstimate = true,
    options?: IGlobalRequestOptions,
  ): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(EstimateActions.fetchEstimate(estimateId));

      if (setCurrentEstimate) {
        dispatch(EstimateActions.setCurrentEstimateId(estimateId));
      }

      try {
        const response = await (options ? load(jobId, estimateId, options) : load(jobId, estimateId));

        dispatch(EstimateActions.receiveEstimate(response.estimate));

        // rsf-api's line-item ext_price is product-only, with no option roll-up, so totals are
        // recomputed bottom-up here.
        dispatch(EstimateEditorActions.recalculateEstimateTotals(estimateId));
        dispatch(EstimateActions.copyEstimateToLastSaved(estimateId));
        dispatch(
          EstimateActions.baselinePaymentTermFinancials(
            estimateId,
            response.estimate.payment_terms?.financed_amount ?? 0,
            response.estimate.payment_terms?.down_payment ?? 0,
          ),
        );
        return true;
      } catch (response) {
        dispatch(EstimateActions.setEstimateLoaded(estimateId));
        if (!lastSavedById(getState())?.get(estimateId)) {
          dispatch(EstimateActions.discardEstimate(estimateId));
        }
        handleErrors(response);
        return false;
      }
    };
  },

  /**
   * Loads the estimate only when it isn't already in the store; getEstimate has no in-flight guard,
   * so callers must guard against duplicate loads.
   *
   * @param jobId
   * @param estimateId
   * @param setCurrentEstimate
   * @returns
   */
  ensureEstimateLoaded: (
    jobId: number,
    estimateId: number,
    setCurrentEstimate = true,
  ): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      if (estimate(getState(), { estimateId })) {
        if (setCurrentEstimate) {
          dispatch(EstimateActions.setCurrentEstimateId(estimateId));
        }
        return true;
      }

      return dispatch(EstimateAsyncActions.getEstimate(jobId, estimateId, setCurrentEstimate));
    };
  },

  getCompilePattern: (patternId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const patternTestDataRecord = patternTestData(getState());

      if (!patternTestDataRecord.job_id) {
        addFlashAlert("danger", "No job selected");
        return;
      }

      if (!patternTestDataRecord.estimate_id) {
        addFlashAlert("danger", "No estimate selected");
        return;
      }

      try {
        const patternRecord = patternSelector(getState(), { patternId });
        dispatch(IdsManagementActions.fetchModel("compilePattern"));
        const response: Record<string, unknown> = await pattern(patternTestDataRecord);

        const compiled_html = handlebars.compile(patternRecord?.view, response);

        dispatch(
          RsfFormActions.update({
            rootPath: ["patterns", "byId", patternId],
            name: "compiled_view",
            value: compiled_html,
          }),
        );
      } catch (response) {
        handleErrors(response);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("compilePattern"));
      }
    };
  },
  /**
   * Emails an estimate/inspection PDF. The estimator passes no `emailParams` and the record is read
   * from the `estimate` form slice; the inspection editor (which has no such form) passes the params
   * directly. The success toast is kind-aware, derived from the params' `doc_type` — inspection reads
   * "Inspection was successfully sent", the estimator keeps the generic "Document was successfully sent".
   */
  sendEstimateEmail: (
    estimateId: IdType,
    navigate: () => void,
    emailParams?: EmailParamsRecord,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        const params = emailParams ?? form(getState(), { name: "estimate" })?.get("emailParams");

        await emailEstimate(estimateId, params);

        const message =
          docTypeKind(params?.get("doc_type")) === "inspection"
            ? "Inspection was successfully sent"
            : "Document was successfully sent";
        addFlashAlert("success", message);
        navigate();
      } catch (response) {
        handleErrors(response);
      }
    };
  },
  attachTokenIntegration: (
    estimateId: IdType,
    integration: TokenKind,
    documentType: "estimate" | "contract",
  ): ThunkResult<Promise<void>> => {
    return async () => {
      try {
        const integrationMessages: Record<string, string> = {
          marketsharp: "Contact in MarketSharp",
          salesforce: "Opportunity in Salesforce",
          job_nimbus: "Job Nimbus",
          jobprogress: "Job Progress",
          lead_perfection: "Lead Perfection",
        };

        await attachIntegration(estimateId, documentType);

        const message = `Successfully attached document to ${integrationMessages[integration] || "integration"}.`;
        addFlashAlert("success", message);
      } catch (response) {
        handleErrors(response, true);
      }
    };
  },
  validateCommission: (estimateId: IdType): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        const custom_config = commissionCustomConfig(getState());
        const required = custom_config?.get("required");
        if (required?.size > 0) {
          if (required.includes("greenline_percentage")) {
            const greenline_percentage = currentOrgSettingsConfig(getState(), {
              path: ["estimator", "commission_calculator", "greenline_percentage"],
            });

            if (!greenline_percentage) {
              addFlashAlert("danger", "Custom Commission Configuration entered requires a Greenline Percentage value");
              return false;
            }
          }
        }

        await validateCommission(estimateId, custom_config);
        addFlashAlert("success", "Commission configuration validated successfully.");
        return true;
      } catch (e) {
        handleErrors(e, true, true);
        return false;
      }
    };
  },

  /**
   * Fetches the server-computed commission breakdown for greenline/custom methods and stores it in the
   * single `calculatedCommission` slot. FETCH seeds an empty loading record; RECEIVE hydrates it. On
   * failure the flash surfaces the error and an empty record clears the loading flag so the modal never
   * hangs or renders a stale breakdown. Formula / margin_with_overhead are pure client-side and
   * never call this.
   */
  calculateEstimateCommission: (estimateId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(EstimateActions.fetchEstimateCommission());
      try {
        const response = await getEstimateCommission(estimateId);
        dispatch(EstimateActions.receiveEstimateCommission(response));
      } catch (error) {
        handleErrors(error);
        dispatch(EstimateActions.receiveEstimateCommission({}));
      }
    };
  },

  duplicateEstimate: (
    estimateId: number,
    navigate: (path: string, opts?: { replace?: boolean }) => void,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        const newDraftId = await dispatch(
          EstimateAsyncActions.cloneEstimateWithAttachments(estimateId, { markSourceLoading: true }),
        );
        await dispatch(EstimateAsyncActions.saveEstimate(newDraftId, navigate));
      } finally {
        dispatch(EstimateActions.setEstimateLoaded(estimateId));
      }
    };
  },

  cloneEstimateWithAttachments: (
    estimateId: number,
    opts: { markSourceLoading?: boolean } = {},
  ): ThunkResult<Promise<number>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const newDraftId = unsavedId(getState());
      dispatch(EstimateEditorActions.duplicateEstimate(estimateId));
      if (opts.markSourceLoading) {
        dispatch(EstimateActions.fetchEstimate(estimateId));
      }
      await dispatch(EstimateEditorAsyncActions.duplicateEstimateAttachments());
      return newDraftId;
    };
  },

  /**
   * A negative `estimateId` creates; anything else updates.
   *
   * @param estimateId
   * @param navigate
   * @param opts
   * @returns
   */
  saveEstimate: (
    estimateId: number,
    navigate: (path: string, opts?: { replace?: boolean }) => void,
    opts: {
      currentPath?: string;
      silent?: boolean;
      skipNavigation?: boolean;
      skipSetCurrentEstimate?: boolean;
      skipDraftCleanup?: boolean;
    } = {},
  ): ThunkResult<Promise<number | undefined>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const state = getState();

      const jobId = currentJobId(state);
      const estimateRecord = estimate(state, { estimateId });
      if (!jobId) {
        addFlashAlert("danger", "Job not found");
        return undefined;
      }
      if (!estimateRecord) {
        addFlashAlert("danger", "Estimate not found");
        return undefined;
      }

      dispatch(EstimateActions.fetchEstimate(estimateId));

      // Capture draft line item UUIDs before save so we can resolve negative-ID
      // line items to their server-assigned IDs for post-save file uploads.
      const draftsByUuid = collectDraftLineItemUuids(state, estimateRecord, estimateGroup, estimateLineItem);

      // Capture pending file uploads (negative-ID file_queue records) BEFORE the
      // save so we still know which files to upload after the line items are
      // assigned real IDs by the server response.
      const pendingFileUploads = collectPendingFileUploads(
        state,
        estimateRecord,
        estimateGroup,
        estimateLineItem,
        documentSelector,
        imageSelector,
      );

      try {
        // Build nested payload with explicit field picks
        const getLineItem = (lineItemId: number) => estimateLineItem(state, { estimateLineItemId: lineItemId });
        const getOption = (optionId: number) => estimateLineItemOption(state, { estimateLineItemOptionId: optionId });
        const getDocument = (documentId: number) => documentsById(state)?.get(documentId) as DocumentRecord | undefined;
        const getImage = (imageId: number) => imagesById(state)?.get(imageId) as ImageRecord | undefined;
        const getOpeningEstimation = (openingEstimationId: number) =>
          openingEstimationSelector(state, { openingEstimationId });
        const currentById = lineItemsById(state) as ImmutableMap<number, EstimateLineItemRecord>;
        const savedById = lastSavedLineItemsById(state) as ImmutableMap<number, EstimateLineItemRecord>;

        const groupsAttributes = estimateRecord.group_ids
          .map((groupId) => {
            const group = estimateGroup(state, { estimateGroupId: groupId });
            return group
              ? transformGroupForPayload(
                  state,
                  group,
                  getLineItem,
                  getOption,
                  getDocument,
                  getImage,
                  getOpeningEstimation,
                  savedById,
                  currentById,
                )
              : null;
          })
          .filter(Boolean)
          .toArray();

        // Use create for new estimates (negative IDs), update for existing ones
        const isNew = estimateId < 0;

        const payload = {
          estimate: {
            ...pickEstimateFields(estimateRecord),
            groups_attributes: groupsAttributes,
            // An empty array is a Rails nested-attributes no-op.
            estimate_finance_options_attributes: toEstimateFinanceOptionsJson(state, {
              estimateFinanceOptionIds: estimateRecord.finance_option_ids,
            }),
          },
        };
        const response = isNew
          ? await create(jobId, payload as IEstimatePayload)
          : await update(jobId, estimateId, payload as IEstimatePayload);

        // Guard against malformed responses before mutating any state
        const savedId = response.estimate.id;
        if (!savedId) {
          addFlashAlert("danger", "Save failed: server returned an invalid response");
          return undefined;
        }

        dispatch(EstimateActions.receiveEstimate(response.estimate));

        // Phase 3 of the file upload lifecycle. See `specs/line-item-editor.md`.
        let succeededUploads = 0;
        let failedUploads = 0;
        const totalUploads = pendingFileUploads.length;

        if (totalUploads > 0) {
          const lineItemIdRemap = buildLineItemIdRemap(response.estimate, draftsByUuid);

          // Re-flag the saved estimate as loading so the Save button stays
          // disabled through the upload phase. RECEIVE_ESTIMATE clears loading
          // via Utils.receive before uploads run; this re-asserts it.
          dispatch(EstimateActions.fetchEstimate(savedId));
          try {
            await Promise.allSettled(
              pendingFileUploads.map(async (pending) => {
                const resolvedLineItemId =
                  pending.lineItemId < 0
                    ? (lineItemIdRemap.get(pending.lineItemId) ?? pending.lineItemId)
                    : pending.lineItemId;
                // Remap miss: the line item didn't come back with a positive id.
                // No upload endpoint exists for a draft id; count as failed and
                // skip — the aggregated flash will report it.
                if (resolvedLineItemId < 0) {
                  failedUploads += 1;
                  return;
                }

                if (pending.kind === "document") {
                  await dispatch(
                    DocumentAsyncActions.batchCreateDocument(
                      [pending.file],
                      {
                        documentableType: "EstimateLineItem",
                        documentableId: resolvedLineItemId,
                        display: pending.display,
                        replacedNegativeIds: [pending.negativeId],
                      },
                      false,
                      SILENT_DOCUMENT_CONTEXT,
                    ),
                  );
                  if (documentsById(getState())?.get(pending.negativeId)) {
                    failedUploads += 1;
                    dispatch(EstimateLineItemActions.pushDocumentId(resolvedLineItemId, pending.negativeId));
                  } else {
                    succeededUploads += 1;
                  }
                } else {
                  // Direct-to-S3 (presign → PUT → createDirect) removes the synchronous-multipart
                  // saturation that made many-photo saves silently need a second save (BR-4). Keeps
                  // the staged record on failure so this same detection re-queues it next save.
                  await dispatch(
                    ImageAsyncActions.batchCreateDirectUploadImages(
                      [pending.file],
                      {
                        imageableType: "EstimateLineItem",
                        imageableId: resolvedLineItemId,
                        display: pending.display,
                        sort_order: pending.sort_order,
                        replacedNegativeIds: [pending.negativeId],
                      },
                      false,
                      SILENT_IMAGE_CONTEXT,
                    ),
                  );
                  if (imagesById(getState())?.get(pending.negativeId)) {
                    failedUploads += 1;
                    dispatch(EstimateLineItemActions.pushImageId(resolvedLineItemId, pending.negativeId));
                  } else {
                    succeededUploads += 1;
                  }
                }
              }),
            );
          } finally {
            dispatch(EstimateActions.setEstimateLoaded(savedId));
          }
        }

        // Clear editing snapshots since save was successful
        // This allows a new edit session to start with fresh snapshots
        dispatch(EstimateEditorActions.commitLineItemSession());

        // Clean up orphaned draft line items (negative IDs) after successful save
        if (!opts.skipDraftCleanup) {
          dispatch(EstimateEditorActions.cleanupDraftLineItems());
        }

        // Recalculate all group and estimate totals from the final byId state, then re-run
        // validations — both via the single afterEstimateMutation chokepoint. The
        // server response may have stale totals; this ensures the UI always reflects the true sum
        // of the saved line items and that validation state is fresh post-save.
        dispatch(EstimateEditorAsyncActions.afterEstimateMutation(savedId));
        // Commit the recalculated group totals into the saved baseline. Now largely redundant on the
        // dirty path (group derived totals are excluded via GROUP_DERIVED_TOTAL_FIELDS, RSF-25619),
        // but kept as belt-and-suspenders on the save path so lastSavedById group totals also reflect
        // the true post-save sum for any non-dirty consumer.
        dispatch(EstimateEditorActions.commitGroupTotalsToLastSaved(savedId));

        if (!opts.silent) {
          addFlashAlert("success", `${kindTitle(estimateRecord.kind)} saved successfully`);
        }

        // Aggregated upload flash — single message instead of per-task spam.
        if (failedUploads > 0) {
          if (succeededUploads === 0) {
            addFlashAlert("danger", `0/${totalUploads} files failed to upload — please save again to retry.`);
          } else {
            addFlashAlert(
              "warning",
              `${succeededUploads}/${totalUploads} files uploaded — please save again to retry the rest.`,
            );
          }
        }

        if (isNew && !opts.skipSetCurrentEstimate) {
          // Switch the current estimate to the real server-assigned ID
          dispatch(EstimateActions.setCurrentEstimateId(savedId));
        }
        // Copy current state to lastSavedById so isDirty returns false. Line items whose
        // image_ids/document_ids were reconciled by the post-save upload fan-out re-baseline
        // themselves: a confirmed positive-id link is recorded in lastSavedById at push time
        // (PUSH_ESTIMATE_LINE_ITEM_{IMAGE,DOCUMENT}_ID), so no per-line-item re-baseline is needed
        // here (RSF-25794 BR-6). Failed uploads keep the staged negative id byId-only → still dirty.
        dispatch(EstimateActions.copyEstimateToLastSaved(savedId));

        if (isNew && !opts.skipNavigation) {
          // Route to the editor for this estimate's kind — an inspection stays on the inspection
          // tab, not the estimator (both share this create → first-save path). A navigation is
          // required because the URL segment is literally `.../estimates/new/...` until the server
          // id exists; when the caller threads its current pathname (EstimateHeader), preserve the
          // rep's current tab (Overview or group tab N) by swapping only the `new` id segment for
          // the saved id rather than forcing `groups/1`. The inspection special-case takes
          // precedence over currentPath.
          const savedKind = estimate(getState(), { estimateId: savedId })?.kind;
          let destination: string;
          if (savedKind === "inspection") {
            destination = `/jobs/${jobId}/inspection/${savedId}`;
          } else if (opts.currentPath) {
            destination = opts.currentPath.replace(/(\/estimates\/)[^/]+/, `$1${savedId}`);
          } else {
            destination = `/jobs/${jobId}/estimates/${savedId}/groups/1`;
          }
          navigate(destination, { replace: true });
        }

        return savedId;
      } catch (error) {
        handleErrors(error);
        return undefined;
      } finally {
        dispatch(EstimateActions.setEstimateLoaded(estimateId));
      }
    };
  },

  calculateFinancing: (
    estimate: EstimateRecord | IEstimateData,
    financedAmount: number,
    financeOptionIds: List<number>,
  ): ThunkResult<void> => {
    return (dispatch: RootDispatchType, getState) => {
      const jobRecord = currentJob(getState());
      if (!jobRecord) {
        return;
      }

      const localFinanceOptions = filteredFinanceOptions(getState(), {
        providers: LocalCalcFinanceOptionProviders,
        financeOptionIds,
        financedAmount,
      });

      if (localFinanceOptions.size > 0) {
        dispatch(EstimateActions.receiveCalculations(financedAmount, localCalculations(localFinanceOptions, estimate)));
      }
    };
  },
};

const pickEstimateFields = (est: EstimateRecord) => {
  const fields: Record<string, unknown> = {
    name: est.name,
    calc_method: est.calc_method,
    activated_price_list_id: est.activated_price_list_id,
    tax_rate: est.tax_rate,
    taxable_amount: est.taxable_amount,
    default_taxable_amount: est.default_taxable_amount,
    product_markup: est.product_markup,
    labor_markup: est.labor_markup,
    // Date-only (YYYY-MM-DD) to avoid a TZ day-shift; sent even when null so clearing a date wipes
    // it server-side. rsf-app persists these by serializing the whole estimate — occ-app's allowlist
    // must name them explicitly or they're dropped from both the create and update payloads.
    estimated_start: est.estimated_start ? formatDateOnly(est.estimated_start) : null,
    estimated_end: est.estimated_end ? formatDateOnly(est.estimated_end) : null,
    // Persist the wizard's saved default options (keyed by option match_id) so Set/Use Options
    // survives a save+reload; est.defaults always exists (record default is defaultsFromJSON({})),
    // and estimateFromJSON re-hydrates it via defaultsFromJSON on the way back (rsf-app parity —
    // it sends defaults unconditionally).
    defaults: est.defaults.toJS(),
    // Persist the computed totals so downstream server-side consumers (PDF/contract generation, CSV
    // exports, reporting) read accurate values rather than stale/zero ones (RSF-25619). The
    // server permits these params and stores them verbatim (no before_save recompute).
    subtotal: est.subtotal,
    total: est.total,
    tax_total: est.tax_total,
    discount: est.discount,
  };

  if (est.kind) {
    fields.kind = est.kind;
  }

  if (est.metadata && est.metadata.size > 0) {
    fields.metadata = est.metadata.toJS();
  }

  if (est.payment_terms) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructure to omit id from the serialized payload
    const { id: ptId, ...ptRest } = est.payment_terms.toJS();
    // Strip id: 0 — Rails would try to look up a non-existent record
    fields.payment_terms = ptId === 0 ? ptRest : est.payment_terms.toJS();
  }

  // Always serialize discounts — including an empty list. Server-side `discounts` is a wholesale JSON
  // column, so omitting the key leaves the stored column untouched; removing the last discount would
  // otherwise never persist (BR-1, RSF-25852). est.discounts always exists (record default: empty List).
  fields.discounts = est.discounts.toJS();

  return fields;
};

const pickGroupFields = (group: EstimateGroupRecord) => {
  const fields: Record<string, unknown> = {
    name: group.name,
    included: group.included,
    sort_order: group.sort_order,
    product_markup: group.product_markup,
    labor_markup: group.labor_markup,
    // Persist group totals for downstream consumers (RSF-25619). Only subtotal/total are sent —
    // the rsf-api estimate_groups table persists only these (rsf-app "standard" calc-method shape); no
    // per-group tax/discount fields and no calc_method behavior are introduced.
    subtotal: group.subtotal,
    total: group.total,
  };

  // Only include ID for persisted records (id > 0)
  if (group.id > 0) {
    fields.id = group.id;
  }

  // Only include _destroy when marking for deletion
  if (group._destroy) {
    fields._destroy = true;
  }

  return fields;
};

const pickLineItemFields = (lineItem: EstimateLineItemRecord) => {
  const fields: Record<string, unknown> = {
    product_id: lineItem.product_id,
    product_uuid: lineItem.product_uuid,
    uuid: lineItem.uuid,
    name: lineItem.name,
    description: lineItem.description,
    quantity: lineItem.quantity,
    uom: lineItem.uom,
    base_product_price: lineItem.base_product_price,
    base_labor_price: lineItem.base_labor_price,
    product_price: lineItem.product_price,
    labor_price: lineItem.labor_price,
    price: lineItem.price,
    ext_price: lineItem.ext_price,
    cost: lineItem.cost,
    msrp: lineItem.msrp,
    discountable: lineItem.discountable,
    markupable: lineItem.markupable,
    visibility: lineItem.visibility,
    sort_order: lineItem.sort_order,
    estimate_group_id: lineItem.estimate_group_id,
    // Persist the product image/doc URLs copied onto the line item at product-selection time.
    // rsf-app sends these (it spreads the whole line item); without them the product image and
    // estimate/contract docs are dropped on save, so they vanish on reload and the server-side
    // doc/PDF consumers (which prioritize *_urls over *_ids) lose them too.
    product_image_urls: lineItem.product_image_urls.toArray(),
    estimate_doc_urls: lineItem.estimate_doc_urls.toArray(),
    contract_doc_urls: lineItem.contract_doc_urls.toArray(),
  };

  // Only include ID for persisted records (id > 0)
  if (lineItem.id > 0) {
    fields.id = lineItem.id;
  }

  // Only include _destroy when marking for deletion
  if (lineItem._destroy) {
    fields._destroy = true;
  }

  return fields;
};

const pickOptionFields = (state: RootState, option: EstimateLineItemOptionRecord) => {
  const optionImageUrls = option.product_option_image_urls.isEmpty()
    ? imageUrlsFromIds(state, option.product_option_image_ids)
    : option.product_option_image_urls;
  const fields: Record<string, unknown> = {
    product_option_id: option.product_option_id,
    product_option_uuid: option.product_option_uuid,
    // Persist the cross-product option identity so the wizard's Set/Use Options feature survives a
    // reload: the Set reducer keys estimate.defaults by this match_id, and after a save+reload the
    // options are re-hydrated from the server — without sending it, the reloaded options carry an
    // empty match_id and Set captures nothing (rsf-app sends the whole option, incl. match_id).
    product_option_match_id: option.product_option_match_id,
    // Persist both so the option's "{group}: {name}" label survives a reload (rsf-app parity); the
    // server otherwise stores a blank product_option_group_name.
    product_option_group_id: option.product_option_group_id,
    product_option_group_name: option.product_option_group_name,
    quantity: option.quantity,
    base_product_price: option.base_product_price,
    base_labor_price: option.base_labor_price,
    product_price: option.product_price,
    labor_price: option.labor_price,
    price: option.price,
    ext_price: option.ext_price,
    // product_factor/labor_factor re-derive percent-UOM base prices. Dropping any of these zeroed
    // them on reload → mis-counted totals and base prices (rsf-app sends the whole option; rsf-api
    // permits all of them in options_attributes). pog_type is intentionally NOT persisted: occ-app
    // can't author cost-type options, and rsf-api's nested attributes preserve any existing
    // server-side value on update. — RSF-25619
    product_factor: option.product_factor,
    labor_factor: option.labor_factor,
    cost: option.cost,
    msrp: option.msrp,
    charge_type: option.charge_type,
    uom: option.uom,
    name: option.name,
    description: option.description,
    // Pre-existing options carry image ids but no urls. rsf-mobile reads product_option_image_ids[0],
    // so the ids are always kept alongside the resolved urls.
    product_option_image_ids: option.product_option_image_ids.toArray(),
    product_option_image_urls: optionImageUrls.toArray(),
  };

  // Only include ID for persisted records (id > 0)
  if (option.id > 0) {
    fields.id = option.id;
  }

  // Only include _destroy when marking for deletion
  if (option._destroy) {
    fields._destroy = true;
  }

  return fields;
};

// Mirrors the rsf-api permit list (id/openable_id/openable_type/_destroy); the client-side
// `openable` record never enters the payload.
const pickOpeningEstimationFields = (openingEstimation: OpeningEstimationRecord) => {
  const fields: Record<string, unknown> = {
    openable_id: openingEstimation.openable_id,
    openable_type: openingEstimation.openable_type,
  };

  // Only include ID for persisted records (id > 0)
  if (openingEstimation.id > 0) {
    fields.id = openingEstimation.id;
  }

  // Only include _destroy when marking for deletion
  if (openingEstimation._destroy) {
    fields._destroy = true;
  }

  return fields;
};

const pickLineItemDocumentFields = (doc: DocumentRecord) => {
  const fields: Record<string, unknown> = {};
  // Only persisted (positive-ID) docs go through documents_attributes.
  // New uploads (negative IDs with file_queue) are flushed after the estimate
  // save via batchCreateDocument so the server can mint a real ID alongside an
  // S3-direct upload, matching the Product save pattern.
  if (doc.id > 0) {
    fields.id = doc.id;
  }
  if (doc.display != null) {
    fields.display = doc.display;
  }
  if (doc._destroy) {
    fields._destroy = true;
  }
  return fields;
};

/**
 * Emits `images_attributes` for a line item's image record. Three cases, gated so normal saves stay
 * byte-identical:
 * - positive id → `{ id, _destroy? }` (persisted image; unchanged).
 * - negative id + source `file` URL + NO `file_queue` (a duplicate clone) → `{ remote_file_url, display,
 *   name }` with NO `id`, so the server copies the source image server-side (item-4 server-copy path).
 * - negative id + `file_queue` File (a user upload) → returns `null` here; it's uploaded post-save via
 *   `batchCreateDirectUploadImages`. The ONLY source of a negative-id-with-URL-and-no-file_queue image is the
 *   duplicate cascade, so normal saves / template apply / ProVia imports (whose negative-id images all
 *   carry a `file_queue`) never emit a `remote_file_url` key.
 */
const pickLineItemImageFields = (img: ImageRecord): Record<string, unknown> | null => {
  if (img.id > 0) {
    const fields: Record<string, unknown> = { id: img.id };
    if (img._destroy) {
      fields._destroy = true;
    }
    return fields;
  }

  // Negative id: only a duplicate clone (URL, no file_queue) is server-copied here. A queued user
  // upload is skipped — batchCreateDirectUploadImages handles it after the save.
  const sourceUrl = img.file?.get("url");
  if (!(img.file_queue instanceof File) && sourceUrl) {
    return { remote_file_url: sourceUrl, display: img.display, name: img.name };
  }
  return null;
};

/**
 * Builds the line item save payload (nested options + persisted-only doc/image
 * attributes). New uploads are skipped here; see `specs/line-item-editor.md`
 * → File Upload Lifecycle.
 */
const transformLineItemForPayload = (
  state: RootState,
  lineItem: EstimateLineItemRecord,
  getOption: (optionId: number) => EstimateLineItemOptionRecord | null | undefined,
  getDocument: (documentId: number) => DocumentRecord | null | undefined,
  getImage: (imageId: number) => ImageRecord | null | undefined,
  getOpeningEstimation: (openingEstimationId: number) => OpeningEstimationRecord | null | undefined,
) => {
  const optionsAttributes = lineItem.option_ids
    .map((optionId) => {
      const option = getOption(optionId);
      return option ? pickOptionFields(state, option) : null;
    })
    .filter(Boolean)
    .toArray();

  const documentsAttributes = (lineItem.document_ids ?? List<number>())
    .map((docId: number) => {
      if (docId < 0) return null;
      const doc = getDocument(docId);
      return doc ? pickLineItemDocumentFields(doc) : null;
    })
    .filter(Boolean)
    .toArray();

  // Unlike documents, negative-id images are NOT blanket-skipped: a duplicate clone (URL, no
  // file_queue) is emitted as a `remote_file_url` server-copy attribute by pickLineItemImageFields.
  // Queued uploads still return null there and upload post-save via batchCreateDirectUploadImages.
  const imagesAttributes = (lineItem.image_ids ?? List<number>())
    .map((imgId: number) => {
      const img = getImage(imgId);
      return img ? pickLineItemImageFields(img) : null;
    })
    .filter(Boolean)
    .toArray();

  // Resolve opening estimations from the normalized slice by id. New (negative-id) attachments ride
  // as create-only nested attributes; soft-deleted persisted rows ride as `{ id, _destroy: true }` so
  // Rails destroys them. Untouched persisted rows are omitted — Rails leaves nested records it isn't
  // sent, so a normal save (no wizard changes) yields an empty array and the key is dropped below,
  // keeping normal saves/template-apply/imports byte-identical.
  const openingEstimationsAttributes = (lineItem.opening_estimation_ids ?? List<number>())
    .map((oeId: number) => {
      const oe = getOpeningEstimation(oeId);
      if (!oe) return null;
      return oe.id < 0 || oe._destroy ? pickOpeningEstimationFields(oe) : null;
    })
    .filter(Boolean)
    .toArray();

  const payload: Record<string, unknown> = {
    ...pickLineItemFields(lineItem),
    options_attributes: optionsAttributes,
  };
  if (documentsAttributes.length > 0) {
    payload.documents_attributes = documentsAttributes;
  }
  if (imagesAttributes.length > 0) {
    payload.images_attributes = imagesAttributes;
  }
  if (openingEstimationsAttributes.length > 0) {
    payload.opening_estimations_attributes = openingEstimationsAttributes;
  }
  return payload;
};

/**
 * Transforms a group record and its line items into API payload format.
 *
 * Handles cross-group moves: persisted items dragged from another group are skipped
 * here (they'll be sent under their source group), and items dragged out of this group
 * are appended with their updated estimate_group_id so Rails can reassign them.
 */
const transformGroupForPayload = (
  state: RootState,
  group: EstimateGroupRecord,
  getLineItem: (lineItemId: number) => EstimateLineItemRecord | null | undefined,
  getOption: (optionId: number) => EstimateLineItemOptionRecord | null | undefined,
  getDocument: (documentId: number) => DocumentRecord | null | undefined,
  getImage: (imageId: number) => ImageRecord | null | undefined,
  getOpeningEstimation: (openingEstimationId: number) => OpeningEstimationRecord | null | undefined,
  lastSavedById: ImmutableMap<number, EstimateLineItemRecord>,
  currentById: ImmutableMap<number, EstimateLineItemRecord>,
) => {
  const groupId = group.id;

  const lineItemsAttributes = group.line_item_ids
    .map((lineItemId) => {
      const lineItem = getLineItem(lineItemId);
      if (!lineItem) return null;
      // Skip persisted items that were moved INTO this group from another group in the DB.
      // Their source group will send them with the updated estimate_group_id.
      if (lineItem.id > 0) {
        const savedGroupId = lastSavedById.get(lineItem.id)?.estimate_group_id;
        if (savedGroupId != null && savedGroupId !== groupId) {
          return null;
        }
      }
      return transformLineItemForPayload(state, lineItem, getOption, getDocument, getImage, getOpeningEstimation);
    })
    .filter(Boolean)
    .toArray();

  // Include persisted items that were moved OUT of this group. Rails can find them
  // in this group's DB association and will update their estimate_group_id.
  const movedOutAttributes: ReturnType<typeof transformLineItemForPayload>[] = [];
  lastSavedById.forEach((savedItem, itemId) => {
    if (savedItem.estimate_group_id === groupId) {
      const currentItem = currentById.get(itemId);
      if (currentItem && currentItem.estimate_group_id !== groupId) {
        movedOutAttributes.push(
          transformLineItemForPayload(state, currentItem, getOption, getDocument, getImage, getOpeningEstimation),
        );
      }
    }
  });

  return {
    ...pickGroupFields(group),
    line_items_attributes: [...lineItemsAttributes, ...movedOutAttributes],
  };
};

/** Phase 2 helper: maps client uuid → negative draft line item id. */
export const collectDraftLineItemUuids = (
  state: Parameters<typeof estimate>[0],
  estimateRecord: EstimateRecord,
  getGroup: typeof estimateGroup,
  getLineItem: typeof estimateLineItem,
): Map<string, number> => {
  const result = new Map<string, number>();

  for (const groupId of estimateRecord.group_ids) {
    const group = getGroup(state, { estimateGroupId: groupId });
    if (!group) continue;

    for (const lineItemId of group.line_item_ids) {
      if (lineItemId >= 0) continue;
      const item = getLineItem(state, { estimateLineItemId: lineItemId });
      if (!item || !item.uuid) continue;
      result.set(item.uuid, lineItemId);
    }
  }

  return result;
};

/** Phase 3 helper: resolves negative line item id → server-assigned positive id by uuid match. */
export const buildLineItemIdRemap = (
  savedEstimate: { groups?: { id: number; line_items?: { id: number; uuid?: string }[] }[] },
  draftsByUuid: Map<string, number>,
): Map<number, number> => {
  const remap = new Map<number, number>();
  if (draftsByUuid.size === 0) return remap;
  if (!savedEstimate.groups) return remap;

  for (const group of savedEstimate.groups) {
    if (!group.line_items) continue;

    for (const lineItem of group.line_items) {
      if (!lineItem.uuid) continue;
      const oldId = draftsByUuid.get(lineItem.uuid);
      if (oldId !== undefined) {
        remap.set(oldId, lineItem.id);
      }
    }
  }

  return remap;
};

interface PendingFileUploadBase {
  lineItemId: number;
  negativeId: number;
  file: File;
  /** The staged record's sort_order, threaded to the direct-upload record so upload order persists (BR-3). */
  sort_order?: Nullable<number>;
}

export interface PendingDocumentUpload extends PendingFileUploadBase {
  kind: "document";
  display: DocumentDisplayType;
}

export interface PendingImageUpload extends PendingFileUploadBase {
  kind: "image";
  display: ImageDisplayType;
}

/** `kind` discriminates `display` so the flush loop reads a typed display without casting. */
export type PendingFileUpload = PendingDocumentUpload | PendingImageUpload;

/** Phase 2 helper: walks the tree for negative-ID doc/image records carrying a `file_queue`. */
export const collectPendingFileUploads = (
  state: Parameters<typeof estimate>[0],
  estimateRecord: EstimateRecord,
  getGroup: typeof estimateGroup,
  getLineItem: typeof estimateLineItem,
  getDocument: typeof documentSelector,
  getImage: typeof imageSelector,
): PendingFileUpload[] => {
  const result: PendingFileUpload[] = [];

  for (const groupId of estimateRecord.group_ids) {
    const group = getGroup(state, { estimateGroupId: groupId });
    if (!group || group._destroy) continue;

    for (const lineItemId of group.line_item_ids) {
      const lineItem = getLineItem(state, { estimateLineItemId: lineItemId });
      if (!lineItem || lineItem._destroy) continue;

      lineItem.document_ids?.forEach((docId: number) => {
        if (docId >= 0) return;
        const doc = getDocument(state, { documentId: docId }) as DocumentRecord | undefined;
        if (doc?.file_queue instanceof File) {
          result.push({
            lineItemId,
            negativeId: docId,
            file: doc.file_queue,
            display: doc.display ?? "both",
            kind: "document",
          });
        }
      });

      lineItem.image_ids?.forEach((imgId: number) => {
        if (imgId >= 0) return;
        const img = getImage(state, { imageId: imgId }) as ImageRecord | undefined;
        if (img?.file_queue instanceof File) {
          result.push({
            lineItemId,
            negativeId: imgId,
            file: img.file_queue,
            display: img.display ?? "both",
            kind: "image",
            sort_order: img.sort_order,
          });
        }
      });
    }
  }

  return result;
};
